import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus, Trash01 } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import {
  TaskTemplatesClient,
  TaskTemplateDto,
  TaskCategoriesClient,
  TaskCategoryDto,
  TaskPrioritiesClient,
  TaskPriorityDto,
} from '../../app/services/api/tasksClient';

const templatesClient = TaskTemplatesClient;
const categoriesClient = TaskCategoriesClient;
const prioritiesClient = TaskPrioritiesClient;

const TaskTemplatesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermissionTo } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['TaskTemplatesView']) : false;
  const canCreate = hasPermissionTo ? hasPermissionTo(['TaskTemplatesCreate']) : false;
  const canEdit = hasPermissionTo ? hasPermissionTo(['TaskTemplatesEdit']) : false;
  const canDelete = hasPermissionTo ? hasPermissionTo(['TaskTemplatesDelete']) : false;

  const [templates, setTemplates] = useState<TaskTemplateDto[]>([]);
  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);

  const [searchName, setSearchName] = useState('');
  const [searchDescription, setSearchDescription] = useState('');
  const [searchCategoryId, setSearchCategoryId] = useState<number | ''>('');
  const [searchPriorityId] = useState<number | ''>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<TaskTemplateDto | null>(null);

  useEffect(() => {
    if (!canView) return;
    // Always refresh data whenever this page is (re)entered
    templatesClient.getTemplates().then(setTemplates).catch(() => setTemplates([]));
    categoriesClient.getCategories().then(setCategories).catch(() => setCategories([]));
    prioritiesClient.getPriorities().then(setPriorities).catch(() => setPriorities([]));
  }, [canView, location.pathname]);

  const handleOpenCreate = () => {
    if (!canCreate) return;
    navigate('/task-templates/new');
  };

  const handleOpenEdit = (row: TaskTemplateDto) => {
    if (!canEdit) return;
    navigate(`/task-templates/${row.id}`);
  };

  const handleRequestDelete = (row: TaskTemplateDto) => {
    if (!canDelete) return;
    setTemplateToDelete(row);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    await templatesClient.deleteTemplate(templateToDelete.id);
    const updated = await templatesClient.getTemplates();
    setTemplates(updated);
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      if (searchName && !t.name.toLowerCase().includes(searchName.toLowerCase())) return false;
      if (searchDescription && !(t.description ?? '').toLowerCase().includes(searchDescription.toLowerCase())) return false;
      if (searchCategoryId && t.taskCategoryId !== Number(searchCategoryId)) return false;
      return true;
    });
  }, [templates, searchName, searchDescription, searchCategoryId, searchPriorityId]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedTemplates = useMemo(
    () => filteredTemplates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredTemplates, page, rowsPerPage],
  );

  const getCategoryName = (id?: number | null) =>
    categories.find(c => c.id === id)?.name ?? '';

  // const getPriorityName = (id: number) =>
  //   priorities.find(p => p.id === id)?.name ?? '';

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>Task Templates</PageTitle>
        <Pill text='You do not have permission to view task templates.' />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle>Task Templates</PageTitle>
        </Grid>

        <Grid item
          xs={12}
          md={9}>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                label='Name'
                fullWidth
                size='small'
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                label='Description'
                fullWidth
                size='small'
                value={searchDescription}
                onChange={e => setSearchDescription(e.target.value)}
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                select
                label='Category'
                fullWidth
                size='small'
                value={searchCategoryId}
                onChange={e => setSearchCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value=''>All</MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.id}
                    value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={3}
          sx={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end', alignItems: 'flex-start' }}>
          {canCreate && (
            <Button
              size='lg'
              variant='primary'
              startIcon={<Plus />}
              onClick={handleOpenCreate}
            >
              Create Template
            </Button>
          )}
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader >Task Templates</TableHeader>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedTemplates.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleOpenEdit(row)}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{getCategoryName(row.taskCategoryId)}</TableCell>
                      <TableCell align='right'
                        onClick={e => e.stopPropagation()}>
                        {canDelete && (
                          <Trash01
                            className='delete-icon'
                            onClick={() => handleRequestDelete(row)}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedTemplates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}
                        style={{ textAlign: 'center' }}>No templates found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      count={filteredTemplates.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>Delete task template?</DialogTitle>
        <DialogContent>
          {`Are you sure you want to delete the template "${templateToDelete?.name ?? ''}"? This action cannot be undone.`}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            variant='primary'
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default TaskTemplatesPage;
